import React from 'react';
import { LucideIcon, CheckCircle2 } from 'lucide-react';
import { useInView } from '../../hooks/useInView';
import { clsx } from 'clsx';

export interface IconGridItem {
  icon: LucideIcon;
  title: string;
  description: string;
  bullets?: string[];
}

interface IconGridProps {
  items: IconGridItem[];
  columns?: 2 | 3 | 4;
  className?: string;
  variant?: 'default' | 'orange';
}

export const IconGrid: React.FC<IconGridProps> = ({
  items,
  columns = 4,
  className,
  variant = 'orange',
}) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const isOrange = variant === 'orange';

  const gridCols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div ref={ref} className={clsx('grid gap-6', gridCols[columns], className)}>
      {items.map((item, i) => (
        <div
          key={item.title}
          className={clsx(
            'transition-all duration-700 ease-out flex flex-col',
            isOrange
              ? 'rounded-3xl border border-orange-200/80 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm hover:shadow-md hover:-translate-y-1'
              : 'text-center sm:text-left',
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          )}
          style={{ transitionDelay: inView ? `${i * 75}ms` : '0ms' }}
        >
          <div className="flex items-center gap-3.5 mb-4">
            <div
              className={clsx(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl font-bold transition-all',
                isOrange
                  ? 'bg-orange-100 dark:bg-orange-950/80 text-orange-600 dark:text-orange-400 ring-1 ring-orange-500/20'
                  : 'bg-black/25 dark:bg-white/10 ring-1 ring-white/20 text-white backdrop-blur'
              )}
            >
              <item.icon className="h-6 w-6" aria-hidden />
            </div>
            <h3
              className={clsx(
                'text-base font-extrabold leading-tight',
                isOrange ? 'text-gray-900 dark:text-white' : 'text-app-text dark:text-white'
              )}
            >
              {item.title}
            </h3>
          </div>

          <p
            className={clsx(
              'text-xs sm:text-sm font-medium leading-relaxed mb-4 flex-1',
              isOrange ? 'text-gray-600 dark:text-gray-300' : 'text-app-text-muted dark:text-gray-400'
            )}
          >
            {item.description}
          </p>

          {item.bullets && item.bullets.length > 0 && (
            <ul className="space-y-2 text-xs font-semibold pt-3 border-t border-orange-100 dark:border-gray-800/80">
              {item.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};
